import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSubjects } from './SubjectContext';
import { StartMode, Subject, YearLevel } from './types';

type Step = { kind: 'catalogue' } | { kind: 'year'; subject: Subject } | { kind: 'start'; subject: Subject; year: YearLevel };

const choiceClass =
  'block w-full rounded-lg border-2 border-gray-200 bg-white p-5 text-left transition-colors hover:border-gray-400 dark:border-gray-700 dark:bg-gray-800';

const AddSubject: React.FC = () => {
  const { catalogue, visible, addSubject } = useSubjects();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'catalogue' });
  const [error, setError] = useState<string | null>(null);
  const available = catalogue.filter((subject) => !visible.some((item) => item.id === subject.id));

  const finish = async (subject: Subject, year: YearLevel, mode: StartMode) => {
    try {
      await addSubject(subject.id, year, mode);
      navigate(`/subjects/${subject.id}/lesson?mode=${mode}`);
    } catch {
      setError('Could not add that subject. Try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 dark:bg-gray-900">
      <div className="mx-auto max-w-xl">
        <Link to="/dashboard" className="text-sm text-gray-600 dark:text-gray-300">
          ← Back
        </Link>
        <h1 className="mb-6 mt-4 text-2xl font-bold text-gray-900 dark:text-white">Add a subject</h1>
        {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

        {step.kind === 'catalogue' && (
          <div className="space-y-3">
            {available.length === 0 && (
              <p className="text-gray-600 dark:text-gray-400">You already have every subject we offer.</p>
            )}
            {available.map((subject) => (
              <button key={subject.id} type="button" className={choiceClass} onClick={() => setStep({ kind: 'year', subject })}>
                <span className="flex items-center justify-between">
                  <span className="text-lg font-semibold" style={{ color: subject.colour }}>
                    {subject.name}
                  </span>
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">{subject.tagline}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {step.kind === 'year' && (
          <div className="space-y-3">
            <p className="text-lg font-medium text-gray-900 dark:text-white">Year 11 or Year 12?</p>
            {([11, 12] as YearLevel[]).map((year) => (
              <button
                key={year}
                type="button"
                className={choiceClass}
                onClick={() => setStep({ kind: 'start', subject: step.subject, year })}
              >
                Year {year}
              </button>
            ))}
          </div>
        )}

        {step.kind === 'start' && (
          <div className="space-y-3">
            <p className="text-lg font-medium text-gray-900 dark:text-white">How do you want to start {step.subject.name}?</p>
            <button type="button" className={choiceClass} onClick={() => finish(step.subject, step.year, 'diagnostic')}>
              <span className="block font-semibold">Short diagnostic</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">A few questions to find your starting unit.</span>
            </button>
            <button type="button" className={choiceClass} onClick={() => finish(step.subject, step.year, 'beginning')}>
              <span className="block font-semibold">Start from the beginning</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">Begin at Unit 1.</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddSubject;
